import { Component } from 'react';

import { graphql } from '@apollo/client/react/hoc';
import { DocumentSearch } from '@stratumn/icons';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import pathToRegexp from 'path-to-regexp';
import PropType from 'prop-types';
import injectSheet from 'react-jss';

import { Navbar } from '@/components/layouts';
import { KanbanHeader, KanbanMain } from '@/components/layouts/kanban';
import { ROUTE_GROUP_FORMS, ROUTE_WORKFLOW_OVERVIEW } from '@/constant/routes'; // Kanban view removed (eslint disable comment on previous line can be removed if Kanban view is once again included in the application)
import { FILTERS_QUERY } from '@/gql/localQueries';
import { deepGet } from '@/utils';
import { orderStages } from '@/utils/stage';
import StageColumn, { fragments as stageColumnFragments } from './column';
import styles from './groupBoard.style';

export class GroupBoard extends Component {
  static propTypes = {
    filtersQuery: PropType.object.isRequired,
    groupQuery: PropType.object.isRequired,
    classes: PropType.object.isRequired,
    history: PropType.object.isRequired,
    match: PropType.object.isRequired
  };

  setDocTitle() {
    const { groupQuery: { groupByRowId: group = {} } = {} } = this.props;
    const { name, workflow: { name: workflowName } = {} } = group;

    if (name) document.title = `${name} - ${workflowName} - Trace`;
  }

  componentDidMount() {
    this.setDocTitle();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.groupQuery !== this.props.groupQuery) this.setDocTitle();
  }

  subscribeToGroup(props) {
    const { groupQuery, match } = props;
    this.unsubscribeFromGroup = groupQuery.subscribeToMore({
      document: subscriptions.group,
      variables: { id: `group:${match.params.id}` },
      updateQuery: (_prev, data) => ({
        groupByRowId: data.subscriptionData.data.listen.relatedNode
      })
    });
  }

  UNSAFE_componentWillMount() {
    this.subscribeToGroup(this.props);
  }

  componentWillUnmount() {
    this.unsubscribeFromGroup();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.filtersQuery.filters === this.props.filtersQuery.filters) {
      return;
    }
    if (this.unsubscribeFromGroup) {
      this.unsubscribeFromGroup();
    }
    this.subscribeToGroup(nextProps);
  }

  shouldComponentUpdate(props) {
    const {
      groupQuery: { loading, groupByRowId: group, error }
    } = props;
    return !loading && (!!group || !!error);
  }

  goToWorkFlowBoard = () => {
    const {
      groupQuery: { groupByRowId: group },
      history
    } = this.props;
    history.push(
      pathToRegexp.compile(ROUTE_WORKFLOW_OVERVIEW)({
        id: group.workflow.rowId
      })
    );
  };

  goToGroupForms = () => {
    const {
      match: { params },
      history
    } = this.props;
    history.push(pathToRegexp.compile(ROUTE_GROUP_FORMS)({ id: params.id }));
  };

  getMenuItems = () => {
    const {
      groupQuery: { loading, groupByRowId: group }
    } = this.props;
    if (loading) return [];
    const formCount = deepGet(group, 'actions.totalCount');
    if (!formCount) return [];

    return [
      {
        label: 'View actions',
        icon: <DocumentSearch />,
        onClick: this.goToGroupForms
      }
    ];
  };

  renderHeader = () => {
    const {
      groupQuery: { loading, groupByRowId: group }
    } = this.props;

    const configHeader = {
      loading,
      bottomLevel: {
        workflowPage: true,
        infoContext: {
          links: [
            {
              label: loading ? null : group.name
            }
          ]
        }
      }
    };

    return <Navbar config={configHeader} />;
  };

  renderMain = () => {
    const {
      groupQuery: { groupByRowId: group },
      classes
    } = this.props;

    const stages = orderStages(group.stages.nodes);
    return (
      <div className={classes.groups}>
        {stages.map(stage => (
          <StageColumn group={group} key={stage.rowId} stage={stage} />
        ))}
      </div>
    );
  };

  render() {
    const {
      groupQuery: { loading }
    } = this.props;
    return (
      <>
        <KanbanHeader>{this.renderHeader()}</KanbanHeader>
        <KanbanMain lightTheme loading={loading}>
          {loading ? null : this.renderMain()}
        </KanbanMain>
      </>
    );
  }
}

export const fragments = {
  group: gql`
    fragment GroupQueryFragment on Group {
      rowId
      label
      canUpdate
      name
      workflow {
        rowId
        name
        config {
          rowId
          initActions
        }
      }
      actions {
        totalCount
      }
      stages {
        nodes {
          type
          ...StageColumnFragment
        }
      }
    }
    ${stageColumnFragments.stage}
  `
};

export const queries = {
  groupQuery: gql`
    query groupQuery($groupId: BigInt!, $search: String) {
      groupByRowId(rowId: $groupId) {
        ...GroupQueryFragment
      }
    }
    ${fragments.group}
  `
};

export const subscriptions = {
  group: gql`
    subscription listenGroup($id: String!, $search: String) {
      listen(topic: $id) {
        relatedNodeId
        relatedNode {
          ...GroupQueryFragment
        }
      }
    }
    ${fragments.group}
  `
};

export default compose(
  graphql(FILTERS_QUERY, {
    name: 'filtersQuery'
  }),
  graphql(queries.groupQuery, {
    name: 'groupQuery',
    options: ({ match }) => ({
      variables: { groupId: match.params.id }
    })
  }),
  injectSheet(styles)
)(GroupBoard);
